/* eslint-disable import/max-dependencies */
import {
  combineReducers,
  applyMiddleware,
  createStore,
  compose,
} from "redux";
import createSagaMiddleware from "redux-saga";
import Immutable from "immutable";
import {
  tableReducer as tables,
  initTableState,
  initLoadingState,
  loadingReducer,
  initNotificationsState,
  notificationsReducer,
  initErrorsState,
  errorsReducer,
} from "@fas/ui-framework";
import type {
  TableState,
  LoadingState,
  NotificationsState,
  ErrorsState,
} from "@fas/ui-framework";
import mainSaga from "./saga";

import {
  ACCOUNT_LIST,
  LINKING_LIST,
  PAGES_LIST,
  REDIRECT_LIST,
  SAVE_REDIRECT_LOADING,
  GET_REDIRECT_LOADING,
  SAVE_LINKING_LOADING,
  GET_LINKING_LOADING,
  ACCOUNT_MODULES_LOADING,
  GET_PAGE_LOADING,
  SAVE_PAGE_LOADING,
  GET_PAGE_LIST_LOADING,
  GET_ACCOUNT_LOADING,
  SAVE_ACCOUNT_LOADING,
  PAGE_EXPORTING_LOADING,
  PAGE_IMPORTING_LOADING,
  PAGE_CLONING_LOADING,
  HEIGHT_LOADING,
  GET_PAGE_VERSION_PREVIEW_LOADING,
} from "../../helpers/constants";
import backupReducer from "../../reducers/backup/reducer";

import accountModules, { initAccountModulesState } from "../../reducers/accountModules";
import type { State as AccountModulesState } from "../../reducers/accountModules";
import accountInfo from "../../reducers/accountInfo/reducer";
import accountUsers from "../../reducers/accountUsers/reducer";
import { initAccountGeneralInfoState } from "../../reducers/accountInfo";
import type { State as GeneralInfoState } from "../../reducers/accountInfo";
import { initSetupAccountUsersState } from "../../reducers/accountUsers";
import type { State as SetupUsersState } from "../../reducers/accountUsers";

import pagesList, { initPagesState } from "../../reducers/pages";
import type { State as PagesListState } from "../../reducers/pages";
import pageInfo, { initPageInfoState } from "../../reducers/pageInfo";
import type { State as PageInfoState } from "../../reducers/pageInfo";
import pageVersions, { initPageVersionsState } from "../../reducers/pageVersions";
import type { State as PageVersionsState } from "../../reducers/pageVersions";
import pageModifiers, { initPageModifiersState } from "../../reducers/pageModifiers";
import type { State as PageModifiersState } from "../../reducers/pageModifiers";
import pageResources, { initPageResourcesState } from "../../reducers/pageResources";
import type { State as PageResourcesState } from "../../reducers/pageResources";
import clonePage, { initClonePageState } from "../../reducers/clonePage";
import type { State as ClonePageState } from "../../reducers/clonePage";
import exportPage, { initExportPageState } from "../../reducers/exportPage";
import type { State as ExportPageState } from "../../reducers/exportPage";
import importPage, { initImportPageState } from "../../reducers/importPage";
import type { State as ImportPageState } from "../../reducers/importPage";
import modifierForm, { initModifierFormState } from "../../reducers/modifierForm";
import type { State as ModifierFormState } from "../../reducers/modifierForm";

import redirect, { initRedirectInfoState } from "../../reducers/redirectInfo";
import type { State as RedirectInfoState } from "../../reducers/redirectInfo";
import type { Redirect as RedirectListItem } from "../../reducers/redirectList";

import linking, { initLinkingInfoState } from "../../reducers/linkingInfo";
import type { State as LinkingInfoState } from "../../reducers/linkingInfo";

import type { LinkingInfo } from "../../components/LinkingInfoForm/types/LinkingInfoForm.types";
import type { Page as PageListItem } from "../../components/PagesList/types/PagesList.types";

export type State = Readonly<{
  loading: LoadingState,
  tables: TableState< PageListItem | RedirectListItem | LinkingInfo>,
  notifications: NotificationsState,
  errors: ErrorsState,

  account: {
    accountInfo: GeneralInfoState,
    accountUsers: SetupUsersState,
    accountModules: AccountModulesState,
  },
  page: {
    pagesList: PagesListState,
    pageInfo: PageInfoState,
    pageVersions: PageVersionsState,
    pageModifiers: PageModifiersState,
    pageResources: PageResourcesState,
    clonePage: ClonePageState,
    exportPage: ExportPageState,
    importPage: ImportPageState,
    modifierForm: ModifierFormState,
  },
  redirect: RedirectInfoState,

  linking: LinkingInfoState,
}>

function mapAppToState(): State {
  return {
    loading: initLoadingState({
      [ACCOUNT_LIST]: false,
      [LINKING_LIST]: false,
      [PAGES_LIST]: false,
      [REDIRECT_LIST]: false,
      [ACCOUNT_MODULES_LOADING]: false,
      [GET_PAGE_LOADING]: false,
      [SAVE_PAGE_LOADING]: false,
      [GET_PAGE_LIST_LOADING]: false,
      [GET_LINKING_LOADING]: false,
      [SAVE_LINKING_LOADING]: false,
      [GET_REDIRECT_LOADING]: false,
      [SAVE_REDIRECT_LOADING]: false,
      [GET_ACCOUNT_LOADING]: false,
      [SAVE_ACCOUNT_LOADING]: false,
      [PAGE_EXPORTING_LOADING]: false,
      [PAGE_IMPORTING_LOADING]: false,
      [HEIGHT_LOADING]: false,
      [GET_PAGE_VERSION_PREVIEW_LOADING]: false,
      [PAGE_CLONING_LOADING]: false,
    }),
    tables: initTableState({
      [ACCOUNT_LIST]: { pageSize: 20 },
      [LINKING_LIST]: { pageSize: 20 },
      [PAGES_LIST]: { pageSize: 20 },
      [REDIRECT_LIST]: { pageSize: 20 },
    }),
    notifications: initNotificationsState(),
    errors: initErrorsState(),

    account: {
      accountInfo: initAccountGeneralInfoState(),
      accountUsers: initSetupAccountUsersState(),
      accountModules: initAccountModulesState(),
    },

    page: {
      pagesList: initPagesState(),
      pageInfo: initPageInfoState(),
      pageVersions: initPageVersionsState(),
      pageModifiers: initPageModifiersState(),
      pageResources: initPageResourcesState(),
      clonePage: initClonePageState(),
      exportPage: initExportPageState(),
      importPage: initImportPageState(),
      modifierForm: initModifierFormState(),
    },

    redirect: initRedirectInfoState(),
    linking: initLinkingInfoState(),
  };
}

export const defaultState = mapAppToState();

export const accountBackupStore: string[] = ["account"];
export const pageBackupStore: string[] = ["page"];
export const redirectBackupStore: string[] = ["redirect"];
export const linkingBackupStore: string[] = ["linking"];

const accountReducer = combineReducers({
  accountInfo,
  accountUsers,
  accountModules,
});

const pageReducer = combineReducers({
  clonePage,
  exportPage,
  importPage,
  pageInfo,
  pageVersions,
  pageModifiers,
  pageResources,
  pagesList,
  modifierForm,
});

const rootReducer = combineReducers({
  loading: loadingReducer,
  notifications: notificationsReducer,
  errors: errorsReducer,
  account: accountReducer,
  tables,
  page: pageReducer,
  linking,
  redirect,
});

const sagaMiddleware = createSagaMiddleware();

// eslint-disable-next-line import/no-anonymous-default-export
export default () => {
  // eslint-disable-next-line no-underscore-dangle, flowtype/require-variable-type
  const composeEnhancers = typeof window === "object" && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({
    serialize: {
      immutable: Immutable,
    },
  }) : compose;

  const store = createStore(
    backupReducer(rootReducer),
    mapAppToState(),
    composeEnhancers(applyMiddleware(
      sagaMiddleware
    ))
  );
  sagaMiddleware.run(mainSaga);
  return store;
};
